import { MouseEvent, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useForm, useFieldArray } from 'react-hook-form';
import { FaPlus, FaTrash } from 'react-icons/fa';
import _ from 'lodash';

import { useAppDispatch } from '../../app/hooks';
import { createClientAction, client_field_options, client_authorized_redirect_uri_options } from './clientSlice';
import { Client, ClientProvider } from '../../types';
import { ClientProviderModal, ClientProviderModalProps } from './ClientProviderModal';
import { generateRandomString } from '../../common/generate-random-string';

type ClientForm = Required<Omit<Client,
  |'client_id'|'client_secret'|'authorized_redirect_uris'
>> & {
  authorized_redirect_uris: {
    id: string;
    name: string;
  }[];
};

const initialState: ClientForm = {
  name: '',
  state: 'enabled',
  description: '',
  full_role_id: '',
  providers: [],
  is_published: false,
  access_token: {
    include_scope: false,
    expires_in: 86400,
  },
  refresh_token: {
    is_enabled: false,
    expires_in: 259200,
  },
  username: {
    can_login: false,
    can_signup: false,
  },
  authorized_redirect_uris: [],
};

type Props = {
  tenant_id: string;
  successCallback: (client_name: string) => void;
  dismissCallback: () => void;
};

type ProviderModalProps = Pick<ClientProviderModalProps, 'show'|'init'|'mode'>;

export function ClientCreateForm({ tenant_id, successCallback, dismissCallback }: Props) {
  const dispatch = useAppDispatch();
  const [api_error, setApiError] = useState<SerializedError|undefined>();
  const [provider_modal_props, setProviderModalProps] = useState<ProviderModalProps>({ show: false, mode: 'edit' });
  const { handleSubmit, control, register, watch, getValues, formState: { errors } } = useForm({
    defaultValues: {
      ...initialState,
      full_role_id: `${tenant_id}/owner`,
    },
  });
  const { fields: providers, update, append } = useFieldArray({
    control,
    name: 'providers',
  });
  const {
    fields: authorized_redirect_uris,
    append: appendAuthorizedRedirectUris,
    remove: removeAuthorizedRedirectUris,
  } = useFieldArray({
    control,
    name: 'authorized_redirect_uris',
  });

  const handleCreate = async(data: ClientForm) => {
    let result = await dispatch(createClientAction({
      tenant_id,
      body: {
        ...data,
        authorized_redirect_uris: data.authorized_redirect_uris.map(item => {
          return item.name;
        }).filter((uri) => uri),
      },
    }));
    if (createClientAction.fulfilled.match(result)) {
      successCallback(getValues('name'));
    } else {
      setApiError(result.error);
      const subscription = watch(() => {
        setApiError(undefined);
        subscription.unsubscribe();
      });
    }
  };
  const handleCancel = (event: MouseEvent) => {
    event.preventDefault();
    dismissCallback();
  };
  const handleAddProviderModal = (e: MouseEvent, provider?: ClientProvider) => {
    e.preventDefault();
    setProviderModalProps({ show: true, init: provider, mode: 'new' });
  };
  const handleEditProviderModal = (e: MouseEvent, provider?: ClientProvider) => {
    e.preventDefault();
    setProviderModalProps({ show: true, init: provider, mode: 'edit' });
  };
  const handleCloseProviderModal = (provider?: ClientProvider) => {
    if (provider) {
      let idx = _.findIndex(providers, ({ name }) => name === provider.name);
      if (idx === -1) {
        append(provider);
      } else {
        update(idx, provider);
      }
    }
    setProviderModalProps({ show: false, mode: 'edit' });
  };

  const handleAddAuthorizedRedirectUri = (e: MouseEvent) => {
    e.preventDefault();
    appendAuthorizedRedirectUris({
      id: generateRandomString(24),
      name: '',
    });
  }
  const handleRemoveAuthorizedRedirectUri = (e: MouseEvent, index: number) => {
    e.preventDefault();
    removeAuthorizedRedirectUris(index);
  }
  return (
    <form onSubmit={handleSubmit(handleCreate)}>
      <fieldset>
        <div className="mb-3 row">
          <label htmlFor="name" className="col-sm-3 col-form-label">Name</label>
          <div className="col-sm-6">
            <input type="text" id="name" className="form-control" autoComplete="off"
              { ...register('name', client_field_options.name) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.name?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="description" className="col-sm-3 col-form-label">Description</label>
          <div className="col-sm-6">
            <textarea id="description" className="form-control" autoComplete="off"
              { ...register('description', client_field_options.description) }
            >
            </textarea>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.description?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="full_role_id" className="col-sm-3 col-form-label">FullRoleId</label>
          <div className="col-sm-6">
            <input id="full_role_id" className="form-control" autoComplete="off"
              { ...register('full_role_id', client_field_options.full_role_id) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.full_role_id?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="access_token_expires_in" className="col-sm-3 col-form-label">AccessToken ExpiresIn(Second)</label>
          <div className="mt-2 col-sm-6">
            <div className="col-sm-6">
              <input id="access_token_expires_in" type="number" className="form-control"
                { ...register('access_token.expires_in', client_field_options.access_token?.expires_in) }
              />
            </div>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.access_token?.expires_in?.message}</span>
        </div>
        <div className="mb-3 row">
          <label className="col-sm-3 col-form-label">AccessToken Include Scope</label>
          <div className="mt-2 col-sm-6">
            <div className="col-sm-6">
              <input type="checkbox" className="form-check-input"
                { ...register('access_token.include_scope', client_field_options.access_token?.include_scope) }
              />
            </div>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.access_token?.include_scope?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="refresh_token_is_enabled" className="col-sm-3 col-form-label">RefreshToken Enabled</label>
          <div className="mt-2 col-sm-6">
            <div className="col-sm-6">
              <input type="checkbox" className="form-check-input"
                { ...register('refresh_token.is_enabled', client_field_options.refresh_token?.is_enabled) }
              />
            </div>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.refresh_token?.is_enabled?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="refresh_token_expires_in" className="col-sm-3 col-form-label">RefreshToken ExpiresIn(Second)</label>
          <div className="mt-2 col-sm-6">
            <div className="col-sm-6">
              <input id="refresh_token_expires_in" type="number" className="form-control"
                { ...register('refresh_token.expires_in', client_field_options.refresh_token?.expires_in) }
              />
            </div>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.refresh_token?.expires_in?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="username_can_login" className="col-sm-3 col-form-label">Username Can Login</label>
          <div className="mt-2 col-sm-6">
            <div className="col-sm-6">
              <input id="username_can_login" type="checkbox" className="form-check-input"
                { ...register('username.can_login', client_field_options.username?.can_login) }
              />
            </div>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.username?.can_login?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="username_can_signup" className="col-sm-3 col-form-label">Username Can Signup</label>
          <div className="mt-2 col-sm-6">
            <div className="col-sm-6">
              <input id="username_can_signup" type="checkbox" className="form-check-input"
                { ...register('username.can_signup', client_field_options.username?.can_signup) }
              />
            </div>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.username?.can_signup?.message}</span>
        </div>
        <div className="mb-3 row">
          <label className="col-sm-3 col-form-label">Is Published</label>
          <div className="mt-2 col-sm-6">
            <div className="col-sm-6">
              <input type="checkbox" className="form-check-input"
                { ...register('is_published', client_field_options.is_published) }
              />
            </div>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.is_published?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="state" className="col-sm-3 col-form-label">Providers</label>
          <div className="mt-2 col-sm-6">
            {providers.map(provider => (
              <a key={provider.name} className="me-1" href="/#" role="button" onClick={(e) => handleEditProviderModal(e, provider)}>
                <span className="badge bg-success px-2 py-1">{provider.name}</span>
              </a>
            ))}
            <a role="button" href="/#" className="mx-1" onClick={handleAddProviderModal}>
              <FaPlus/>
            </a>
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="state" className="col-sm-3 col-form-label">Authorized redirect URIs</label>
          <div className="mt-2 col-sm-9 row">
            {authorized_redirect_uris.map((authorized_redirect_uri, index) => (
              <div key={authorized_redirect_uri.id}>
                <div className="col-sm-9">
                  <div className="input-group mb-3">
                    <input type="text" className="form-control" autoComplete="off"
                      { ...register(`authorized_redirect_uris.${index}.name` as const, client_authorized_redirect_uri_options.name) }
                    />
                    <button className="btn btn-outline-secondary" type="button" onClick={(event) => handleRemoveAuthorizedRedirectUri(event, index)}>
                      <FaTrash/>
                    </button>
                  </div>
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.authorized_redirect_uris?.[index].name?.message}</span>
              </div>
            ))}
            <a role="button" href="/#" className="mx-1" onClick={handleAddAuthorizedRedirectUri}>
              <FaPlus/>
            </a>
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="client_state" className="col-sm-3 col-form-label">State</label>
          <div className="col-sm-6">
            <select id="client_state" className="form-select"
              { ...register('state') }
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
        <div className="d-flex">
          <span>{api_error?.message ?? api_error?.name}</span>
        </div>
        <div className="d-flex my-3 justify-content-center">
          <button type="submit" className="btn btn-primary mx-3">Create</button>
          <button className="btn btn-light mx-1" onClick={(e) => handleCancel(e)}>Cancel</button>
        </div>
      </fieldset>
      <ClientProviderModal
        {...provider_modal_props}
        onClose={handleCloseProviderModal}/>
    </form>
  );
}
